import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import AppCard from "@/components/AppCard";
import { Shield, Zap, Layers, Send, ArrowRight, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contactForm, setContactForm] = useState({ name: "", email: "", message: "" });
  const [contactStatus, setContactStatus] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/apps`)
      .then((res) => setApps(res.data))
      .catch((err) => console.error("Failed to load apps:", err))
      .finally(() => setLoading(false));
  }, []);

  const handleContact = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, contactForm);
      setContactStatus("success");
      setContactForm({ name: "", email: "", message: "" });
    } catch {
      setContactStatus("error");
    } finally {
      setSending(false);
      setTimeout(() => setContactStatus(null), 4000);
    }
  };

  const featured = apps.find((a) => a.status === "Featured");
  const otherApps = apps.filter((a) => a.status !== "Featured");

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      <Header />

      {/* Hero */}
      <section data-testid="hero-section" className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-20%,rgba(59,130,246,0.15),transparent)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(14,165,233,0.08),transparent)]" />
        <div className="mx-auto max-w-7xl px-8 py-32 md:py-40">
          <div className="grid gap-16 md:grid-cols-2 md:items-center">
            <div className="animate-slide-in-left">
              <div className="mb-6 inline-flex items-center gap-2.5 rounded-full border border-blue-400/20 bg-blue-950/40 px-5 py-2 backdrop-blur-sm">
                <div className="h-2 w-2 rounded-full bg-blue-400 animate-pulse" />
                <span className="text-xs font-semibold uppercase tracking-wider text-blue-200">
                  Enterprise Portfolio
                </span>
              </div>
              <h1 className="mb-6 text-5xl font-bold tracking-tight leading-[1.1] md:text-6xl lg:text-7xl">
                Digital products for
                <span className="block mt-2 bg-gradient-to-r from-blue-400 via-blue-500 to-indigo-400 bg-clip-text text-transparent">
                  real-world impact
                </span>
              </h1>
              <p className="mb-10 max-w-xl text-lg leading-relaxed text-slate-300">
                Perfect Peace Enterprise delivers mission-critical solutions across operations, safety, productivity, and community engagement.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="#apps"
                  data-testid="hero-explore-btn"
                  className="group inline-flex items-center gap-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 px-8 py-4 text-base font-semibold text-white shadow-lg shadow-blue-900/50 transition-all hover:shadow-xl hover:shadow-blue-900/60 hover:scale-[1.02] active:scale-[0.98]"
                >
                  Explore Our Portfolio
                  <ArrowRight size={18} className="transition-transform group-hover:translate-x-0.5" />
                </a>
                <a
                  href="#contact"
                  data-testid="hero-contact-btn"
                  className="inline-flex items-center gap-2.5 rounded-xl border border-slate-700 bg-slate-800/50 px-8 py-4 text-base font-semibold text-white backdrop-blur-sm transition-all hover:bg-slate-800 hover:border-slate-600"
                >
                  Get in Touch
                </a>
              </div>
            </div>

            {/* Featured preview cards */}
            <div className="grid gap-5 sm:grid-cols-2 animate-fade-in-up">
              {(loading ? Array(4).fill(null) : apps.slice(0, 4)).map((app, i) =>
                app ? (
                  <Link
                    key={app.slug}
                    to={`/apps/${app.slug}`}
                    data-testid={`hero-card-${app.slug}`}
                    className={`group rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900/90 to-slate-900/50 p-6 backdrop-blur-sm transition-all duration-300 hover:border-blue-500/40 hover:shadow-lg hover:shadow-blue-900/20 stagger-${i + 1}`}
                  >
                    <div className="mb-4 flex items-center justify-between gap-2">
                      <span className="rounded-lg bg-slate-800/80 px-3 py-1.5 text-[10px] font-medium uppercase tracking-wider text-slate-400">
                        {app.category}
                      </span>
                      <span className={`text-[10px] font-semibold uppercase tracking-wider ${app.status === "Featured" ? "text-blue-400" : "text-slate-500"}`}>
                        {app.status}
                      </span>
                    </div>
                    <h3 className="mb-2 text-base font-bold text-white">{app.name}</h3>
                    <p className="text-xs leading-relaxed text-slate-400 line-clamp-2">
                      {app.description}
                    </p>
                    <div className="mt-4 flex items-center gap-1.5 text-xs font-semibold text-blue-400 opacity-0 transition-opacity group-hover:opacity-100">
                      <span>Learn More</span>
                      <ChevronRight size={14} />
                    </div>
                  </Link>
                ) : (
                  <div
                    key={i}
                    className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6 animate-pulse"
                  >
                    <div className="h-4 w-20 rounded bg-slate-800 mb-4" />
                    <div className="h-5 w-32 rounded bg-slate-800 mb-3" />
                    <div className="h-3 w-full rounded bg-slate-800/60" />
                    <div className="h-3 w-3/4 rounded bg-slate-800/60 mt-2" />
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section
        id="about"
        data-testid="about-section"
        className="border-y border-slate-800 bg-slate-900/30 backdrop-blur-sm"
      >
        <div className="mx-auto max-w-7xl px-8 py-24">
          <div className="grid gap-16 md:grid-cols-3">
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-[0.3em] text-blue-400">
                About Us
              </p>
              <h2 className="text-3xl font-bold tracking-tight leading-tight lg:text-4xl">
                Building a portfolio of purpose-driven solutions
              </h2>
            </div>
            <div className="md:col-span-2">
              <p className="text-lg leading-relaxed text-slate-300">
                Perfect Peace Enterprise develops practical digital products across operations, safety, preparedness, finance, productivity, care, and community. Each application is designed to solve meaningful real-world problems for the people and organizations that need them most.
              </p>
              <p className="mt-6 text-base leading-relaxed text-slate-400">
                This platform presents our complete product lineup in a unified format, enabling customers, partners, and stakeholders to understand our mission and capabilities at a glance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured App */}
      {featured && (
        <section data-testid="featured-section" className="mx-auto max-w-7xl px-8 py-24">
          <p className="mb-8 text-xs font-bold uppercase tracking-[0.3em] text-blue-400">
            Featured Product
          </p>
          <Link
            to={`/apps/${featured.slug}`}
            data-testid="featured-app-link"
            className="group block rounded-3xl border border-blue-500/20 bg-gradient-to-br from-blue-950/40 via-slate-900/80 to-slate-900/40 p-10 md:p-12 backdrop-blur-sm transition-all hover:border-blue-500/40 hover:shadow-2xl hover:shadow-blue-900/30"
          >
            <div className="grid gap-10 md:grid-cols-2 md:items-center">
              <div>
                <span className="inline-flex items-center gap-2.5 rounded-xl border border-blue-400/20 bg-blue-950/50 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-blue-200 mb-6">
                  <Zap size={14} />
                  {featured.category}
                </span>
                <h3 className="text-4xl font-bold tracking-tight text-white mb-3">
                  {featured.name}
                </h3>
                <p className="text-base font-medium text-blue-300/80 italic mb-6">
                  "{featured.tagline}"
                </p>
                <p className="text-base leading-relaxed text-slate-300">
                  {featured.description}
                </p>
                <div className="mt-8 inline-flex items-center gap-2.5 text-base font-semibold text-blue-400 transition-all group-hover:gap-3.5">
                  <span>Explore This Product</span>
                  <ArrowRight size={18} />
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                {featured.features?.slice(0, 6).map((feat) => (
                  <div
                    key={feat}
                    className="rounded-xl border border-slate-700 bg-slate-800/50 px-5 py-3.5 text-sm font-medium text-slate-200 backdrop-blur-sm"
                  >
                    {feat}
                  </div>
                ))}
              </div>
            </div>
          </Link>
        </section>
      )}

      {/* All Apps */}
      <section id="apps" data-testid="apps-section" className="mx-auto max-w-7xl px-8 py-24">
        <div className="mb-14 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.3em] text-blue-400">
              Our Products
            </p>
            <h2 className="text-4xl font-bold tracking-tight md:text-5xl">
              Complete product ecosystem
            </h2>
          </div>
          <p className="max-w-md text-base leading-relaxed text-slate-400">
            Click any application to explore features, target audience, and strategic vision.
          </p>
        </div>

        {loading ? (
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {Array(6)
              .fill(null)
              .map((_, i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-slate-800 bg-slate-900/50 p-7 animate-pulse"
                >
                  <div className="h-3 w-16 rounded bg-slate-800 mb-4" />
                  <div className="h-6 w-40 rounded bg-slate-800 mb-5" />
                  <div className="h-3 w-full rounded bg-slate-800/60" />
                  <div className="h-3 w-2/3 rounded bg-slate-800/60 mt-2" />
                </div>
              ))}
          </div>
        ) : (
          <div
            data-testid="apps-grid"
            className="grid gap-6 md:grid-cols-2 xl:grid-cols-3"
          >
            {apps.map((app, i) => (
              <div key={app.slug}>
                <AppCard app={app} index={i} />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Why Us */}
      <section
        id="why-us"
        data-testid="why-section"
        className="border-y border-slate-800 bg-slate-900/30 backdrop-blur-sm"
      >
        <div className="mx-auto max-w-7xl px-8 py-24">
          <div className="mb-14 max-w-3xl">
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.3em] text-blue-400">
              Why Perfect Peace Enterprise
            </p>
            <h2 className="text-4xl font-bold tracking-tight md:text-5xl">
              Mission-driven products built for real impact
            </h2>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {[
              {
                icon: Shield,
                title: "Purpose-Driven Development",
                text: "Our portfolio spans business operations, emergency readiness, productivity, care, and personal development — each product rooted in solving meaningful challenges.",
              },
              {
                icon: Layers,
                title: "Clear Value Communication",
                text: "Every product is presented with precision and clarity, enabling stakeholders to quickly understand capabilities, target users, and strategic value.",
              },
              {
                icon: Zap,
                title: "Enterprise-Ready Platform",
                text: "Our platform and products are architected to scale — with comprehensive documentation, case studies, pricing transparency, and live demonstration capabilities.",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="group rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900/80 to-slate-900/40 p-8 backdrop-blur-sm transition-all duration-300 hover:border-blue-500/30 hover:shadow-lg hover:shadow-blue-900/10"
              >
                <div className="mb-5 inline-flex rounded-xl border border-slate-700 bg-slate-800/50 p-4 transition-all duration-300 group-hover:border-blue-500/30 group-hover:bg-blue-950/30">
                  <item.icon size={24} className="text-slate-400 transition-colors group-hover:text-blue-400" />
                </div>
                <h3 className="mb-3 text-xl font-bold text-white">{item.title}</h3>
                <p className="text-sm leading-relaxed text-slate-400">
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section
        id="contact"
        data-testid="contact-section"
        className="mx-auto max-w-4xl px-8 py-28"
      >
        <div className="text-center mb-14">
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.3em] text-blue-400">
            Contact
          </p>
          <h2 className="mb-4 text-4xl font-bold tracking-tight md:text-5xl">
            Let's connect
          </h2>
          <p className="mx-auto max-w-2xl text-lg leading-relaxed text-slate-300">
            Have questions about our products, partnerships, or capabilities? We're here to help.
          </p>
        </div>

        <form
          onSubmit={handleContact}
          data-testid="contact-form"
          className="rounded-3xl border border-slate-800 bg-gradient-to-br from-slate-900/80 to-slate-900/40 p-10 backdrop-blur-sm space-y-6"
        >
          <div className="grid gap-6 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-bold text-slate-300 mb-3 uppercase tracking-wider">
                Name
              </label>
              <input
                type="text"
                required
                data-testid="contact-name-input"
                value={contactForm.name}
                onChange={(e) =>
                  setContactForm({ ...contactForm, name: e.target.value })
                }
                className="w-full rounded-xl border border-slate-700 bg-slate-800/50 px-5 py-4 text-base text-white placeholder-slate-500 outline-none transition-all focus:border-blue-500 focus:bg-slate-800 focus:ring-2 focus:ring-blue-500/20"
                placeholder="Your name"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-300 mb-3 uppercase tracking-wider">
                Email
              </label>
              <input
                type="email"
                required
                data-testid="contact-email-input"
                value={contactForm.email}
                onChange={(e) =>
                  setContactForm({ ...contactForm, email: e.target.value })
                }
                className="w-full rounded-xl border border-slate-700 bg-slate-800/50 px-5 py-4 text-base text-white placeholder-slate-500 outline-none transition-all focus:border-blue-500 focus:bg-slate-800 focus:ring-2 focus:ring-blue-500/20"
                placeholder="you@company.com"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-300 mb-3 uppercase tracking-wider">
              Message
            </label>
            <textarea
              required
              rows={5}
              data-testid="contact-message-input"
              value={contactForm.message}
              onChange={(e) =>
                setContactForm({ ...contactForm, message: e.target.value })
              }
              className="w-full rounded-xl border border-slate-700 bg-slate-800/50 px-5 py-4 text-base text-white placeholder-slate-500 outline-none transition-all focus:border-blue-500 focus:bg-slate-800 focus:ring-2 focus:ring-blue-500/20 resize-none"
              placeholder="Tell us about your needs..."
            />
          </div>
          <div className="flex items-center justify-between gap-4 pt-4">
            <a
              href="mailto:Support@perfectpeaceenterprise.com"
              className="text-sm text-slate-400 hover:text-slate-200 transition-colors"
            >
              Support@perfectpeaceenterprise.com
            </a>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-btn"
              className="inline-flex items-center gap-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 px-8 py-4 text-base font-semibold text-white shadow-lg shadow-blue-900/50 transition-all hover:shadow-xl hover:shadow-blue-900/60 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? "Sending..." : "Send Message"}
              <Send size={16} />
            </button>
          </div>
          {contactStatus === "success" && (
            <p data-testid="contact-success" className="text-sm text-emerald-400 text-center pt-2 font-medium">
              ✓ Message sent successfully! We'll be in touch soon.
            </p>
          )}
          {contactStatus === "error" && (
            <p data-testid="contact-error" className="text-sm text-red-400 text-center pt-2 font-medium">
              Something went wrong. Please try again.
            </p>
          )}
        </form>
      </section>

      {/* Footer */}
      <footer
        data-testid="site-footer"
        className="border-t border-slate-800 bg-slate-900/50 backdrop-blur-sm"
      >
        <div className="mx-auto max-w-7xl px-8 py-12 flex flex-col gap-8 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            <img
              src="https://customer-assets.emergentagent.com/job_c83bb9d1-4481-4216-be0b-e2d2e530a3a4/artifacts/j5gcpl1b_Peaceful%20harmony%20in%20a%20logo%20design.png"
              alt="Perfect Peace Enterprise Logo"
              className="h-12 w-auto object-contain"
            />
            <div>
              <div className="text-lg font-bold text-white">
                Perfect Peace Enterprise
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Building digital products for real-world impact
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-8 text-sm text-slate-400">
            <a href="#apps" className="hover:text-white transition-colors font-medium">
              Products
            </a>
            <a href="#about" className="hover:text-white transition-colors font-medium">
              About
            </a>
            <Link to="/founder" className="hover:text-white transition-colors font-medium">
              Founder
            </Link>
            <a href="#contact" className="hover:text-white transition-colors font-medium">
              Contact
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
